// Logs.js
import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { toKST } from '../../utils/date';
import '../../styles/common.css';
import '../Users/User.css';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useMinLoadingDisplay } from '../../hooks/useMinLoadingDisplay';
import { ScrollToTop } from '../../components/ScrollToTop/ScrollToTop';

const PAGE_SIZE_KEY = 'admin_logs_pageSize';

const Logs = ({ title = '검색관리' }) => {
    const [histories, setHistories] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const saved = localStorage.getItem('PAGE_SIZE_KEY');
        return saved ? parseInt(saved, 10) : 50;
    });
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const showListLoading = useMinLoadingDisplay(isLoading);

    const fetchHistories = async (
        page = currentPage,
        search = searchTerm,
        type = typeFilter,
        sort = sortOrder,
        limit = itemsPerPage
    ) => {
        setIsLoading(true);
        try {
            const res = await api.get('/logs/histories', {
                params: { page, limit, search, type, sort },
            });

            if (res.data.success) {
                setHistories(res.data.histories);
                setTotalPages(Math.ceil(res.data.total / limit));
            } else {
                toast.error(
                    res.data.message || '검색로그를 불러오지 못했습니다.'
                );
                console.error('데이터를 불러오지 못했습니다:', res.data.message);
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message || '검색로그를 불러오는 중 오류가 발생했습니다.';
            toast.error(msg);
            console.error('검색로그 데이터 가져오기 실패:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = e => setSearchTerm(e.target.value);
    const handleTypeFilter = e => {
        setTypeFilter(e.target.value);
        setCurrentPage(1);
    };
    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setCurrentPage(1);
            fetchHistories(1, searchTerm, typeFilter, sortOrder);
        }
    };
    const resetSearch = () => {
        setSearchTerm('');
        setTypeFilter('');
        setSortOrder('');
        setCurrentPage(1);
        fetchHistories(1, '', '', '');
    };

    const handlePageChange = page => setCurrentPage(page);

    const handleItemsPerPageChange = e => {
        const val = parseInt(e.target.value, 10);
        setItemsPerPage(val);
        localStorage.setItem('PAGE_SIZE_KEY', val);
        setCurrentPage(1);
        fetchHistories(1, searchTerm, typeFilter, sortOrder, val);
    };

    const renderPagination = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 5);
        let end = Math.min(totalPages, start + 9);
        if (end - start < 9 && totalPages > 10) start = Math.max(1, end - 9);
        for (let i = start; i <= end; i++) pages.push(i);

        const pageBtn = (page, label, disabled = false, icon = false) => (
            <li className={`page-item ${disabled ? 'disabled' : ''}`}>
                <Link
                    className="page-link"
                    onClick={e => {
                        e.preventDefault();
                        if (!disabled) handlePageChange(page);
                    }}
                    title={icon ? label : undefined}
                >
                    {icon ? <i className={label}></i> : label}
                </Link>
            </li>
        );

        return (
            <nav aria-label="검색 내역 페이지네이션">
                <ul className="pagination pagination-sm justify-content-center mt-3">
                    {pageBtn(1, 'fas fa-angle-double-left', currentPage === 1, true)}
                    {pageBtn(currentPage - 1, '이전', currentPage === 1)}
                    {pages.map(num => (
                        <li
                            key={num}
                            className={`page-item ${currentPage === num ? 'active' : ''}`}
                        >
                            <Link
                                className="page-link"
                                onClick={e => {
                                    e.preventDefault();
                                    handlePageChange(num);
                                }}
                            >
                                {num}
                            </Link>
                        </li>
                    ))}
                    {pageBtn(currentPage + 1, '다음', currentPage === totalPages)}
                    {pageBtn(
                        totalPages,
                        'fas fa-angle-double-right',
                        currentPage === totalPages,
                        true
                    )}
                </ul>
            </nav>
        );
    };

    useEffect(() => {
        fetchHistories();
    }, [currentPage]);

    const resolveTypeLabel = type => {
        switch (type?.toLowerCase()) {
            case 'findnaverranktracking':   return 'N쇼핑 순위추적';
            case 'rankhistorywatch':        return '과거 순위 조회';
            case 'productanalysis':         return 'N상품종합분석';
            case 'productgenerate':         return '상품명키워드생성';
            case 'autocomplete':            return 'N자완추출';
            case 'findnaverrank':           return 'N쇼핑 간편 순위조회';
            case 'findmid':                 return '상품정보 조회(링크만)';
            case 'findproductbymid':        return '상품정보 조회(키워드,미드만)';
            case 'integratedsearch':        return '통검작업키추출(cpc 상관x)';
            case 'keywordrankvolumecheck':  return '통검작업키추출(cpc 상관o)';
            case 'keywordver1':
            case 'keyword1':               return '키워드 추출순위 ver1';
            case 'keywordver2':
            case 'keyword2':               return '키워드 추출순위 ver2';
            case 'naverintegratedrank':     return '통검 순위 체킹';
            case 'changeurl':              return '상품 주소 변환';
            case 'counttext':              return '검색량조회+글자수세기';
            case 'naversales':
            case 'sales':                  return 'N 매출 잔여';
            case 'index':                  return 'N 지수 잔여';
            default:                       return type;
        }
    };

    return (
        <>
            <div id="wrapper">
                <Navi title={title} />
                <LeftSide />
                <div className="content-page">
                    <div className="content">
                        <div className="container-fluid users-admin-page">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-3">
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={typeFilter}
                                                        onChange={handleTypeFilter}
                                                        style={{ width: '180px' }}
                                                    >
                                                        <option value="">조회 구분</option>
                                                        <option value="findNaverRankTracking">N쇼핑 순위추적</option>
                                                        <option value="rankHistoryWatch">과거 순위 조회</option>
                                                        <option value="productAnalysis">N상품종합분석</option>
                                                        <option value="productGenerate">상품명키워드생성</option>
                                                        <option value="autoComplete">N자완추출</option>
                                                        <option value="findnaverrank">N쇼핑 간편 순위조회</option>
                                                        <option value="findmid">상품정보 조회(링크만)</option>
                                                        <option value="findProductByMid">상품정보 조회(키워드,미드만)</option>
                                                        <option value="integratedSearch">통검작업키추출(cpc 상관x)</option>
                                                        <option value="keywordRankVolumeCheck">통검작업키추출(cpc 상관o)</option>
                                                        <option value="keywordver1">키워드 추출순위 ver1</option>
                                                        <option value="keywordver2">키워드 추출순위 ver2</option>
                                                        <option value="naverIntegratedRank">통검 순위 체킹</option>
                                                        <option value="changeUrl">상품 주소 변환</option>
                                                        <option value="countText">검색량조회+글자수세기</option>
                                                        <option value="sales">N 매출 잔여</option>
                                                    </select>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="사용자 검색"
                                                        value={searchTerm}
                                                        onChange={handleSearch}
                                                        onKeyPress={handleKeyPress}
                                                        style={{ width: '200px' }}
                                                    />
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() =>
                                                            fetchHistories(
                                                                1,
                                                                searchTerm,
                                                                typeFilter,
                                                                sortOrder
                                                            )
                                                        }
                                                    >
                                                        <i className="fas fa-search" /> 검색
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={resetSearch}
                                                    >
                                                        <i className="fas fa-sync-alt" /> 초기화
                                                    </button>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <span className="text-muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>페이지당</span>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={itemsPerPage}
                                                        onChange={handleItemsPerPageChange}
                                                        style={{ width: 80 }}
                                                    >
                                                        {[50, 100, 250, 500, 1000].map(n => (
                                                            <option key={n} value={n}>{n}개</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div
                                                className="table-responsive"
                                                style={{ overflowX: 'auto' }}
                                            >
                                                {showListLoading ? (
                                                    <div className="text-center p-4">
                                                        <div
                                                            className="loading-spinner loading-spinner-lg"
                                                            role="status"
                                                            aria-live="polite"
                                                        >
                                                            <span className="visually-hidden">
                                                                데이터를 불러오는 중입니다.
                                                            </span>
                                                        </div>
                                                        <p className="mt-2">
                                                            데이터를 불러오는 중입니다...
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <table className="table table-bordered table-hover table-sm align-middle">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th
                                                                    className="text-center text-nowrap"
                                                                    width="6%"
                                                                >
                                                                    번호
                                                                </th>
                                                                <th
                                                                    className="text-center text-nowrap"
                                                                    width="12%"
                                                                >
                                                                    사용자 아이디
                                                                </th>
                                                                <th
                                                                    className="text-center text-nowrap"
                                                                    width="15%"
                                                                >
                                                                    조회 구분
                                                                </th>
                                                                <th className="text-center" width="57%">
                                                                    내역
                                                                </th>
                                                                <th
                                                                    className="text-center text-nowrap"
                                                                    width="15%"
                                                                >
                                                                    조회 날짜
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {histories.map((h, idx) => (
                                                                <tr key={h.id}>
                                                                    <td className="text-center text-nowrap">
                                                                        {totalPages > 0
                                                                            ? (totalPages -
                                                                                  currentPage) *
                                                                                  itemsPerPage +
                                                                              (histories.length -
                                                                                  idx)
                                                                            : 0}
                                                                    </td>
                                                                    <td className="text-center text-nowrap users-admin-col-id">
                                                                        {h.user_id} ({h.user_name})
                                                                    </td>
                                                                    <td className="text-center text-nowrap">
                                                                        {resolveTypeLabel(h.type)}
                                                                    </td>
                                                                    <td className="text-start users-admin-cell-wrap">
                                                                        {h.input}
                                                                    </td>
                                                                    <td className="text-center text-nowrap">
                                                                        {toKST(
                                                                            h.updated_at ||
                                                                                h.searched_at
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>

                                            {!showListLoading && renderPagination()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <RightBar />
            <div className="rightbar-overlay" />
            <ScrollToTop />
        </>
    );
};

export { Logs };
